import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { defaultBasketData } from 'constant';

import {
  CartStore,
  cleanupStalePreferences,
  clearCart,
  clearPromoDetails,
  removePromoDetails,
  setPromoDetails
} from 'store/reducers/cartSlice';

import {
  AddCreditCardMethodParameters,
  AddGiftCardMethodParameters,
  AddShippingMethodParameters,
  AddShoppingCartBulkParameters,
  AddShoppingCartParameters,
  CartShippingResponseType,
  InvoiceItem,
  PromoErrorType,
  PurchaseHistoryItem,
  PurchaseHistoryParams,
  RemoveCreditCardMethodParameters,
  RemoveGiftCardMethodParameters,
  ShopCartItem,
  ShopCartType,
  ShopCartUpdateResponse,
  ShoppingCartResponse,
  ShoppingItemCartParams,
  ShopPromoParamsType,
  ShopPromoResponseType,
  UpdateShoppingCartParameters
} from 'types';

/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
import api from '../api/cart.api';

const processArrays = (
  oldState: UpdateShoppingCartParameters[],
  newState: ShoppingItemCartParams[]
) => {
  const mainMap = new Map(oldState.map((item) => [item.item_id, item]));
  const updatedItems: UpdateShoppingCartParameters[] = [];
  const newItems: ShoppingItemCartParams[] = [];

  newState.filter((item) => {
    if (mainMap.has(item.item_id)) {
      const mainItem = mainMap.get(item.item_id);
      if (mainItem?.item_id) {
        mainItem.qty += item.qty;
        updatedItems.push({
          invoice_id: item.invoice_id,
          item_id: item.item_id,
          item_type: item.item_type,
          qty: mainItem?.qty,
          cart_item_id: mainItem.cart_item_id
        });
      }

      return false;
    }
    newItems.push(item);
    return true;
  });

  return {
    add: newItems,
    update: updatedItems
  };
};

export const shoppingCartApi = createApi({
  reducerPath: 'shoppingCartApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/' }),
  tagTypes: ['getShoppingCart'],
  endpoints: (builder) => ({
    getShoppingCart: builder.query<ShoppingCartResponse, string>({
      queryFn: async (_, { getState, dispatch }) => {
        const { cart } = getState() as { cart: CartStore };
        try {
          const { data } = await api.getShoppingCartList();

          if (
            (cart?.selectedPackages ?? []).length > 0 ||
            (cart?.selectedProducts ?? []).length > 0
          ) {
            const packages: ShoppingItemCartParams[] = (
              cart?.selectedPackages ?? []
            ).map((pckg) => ({
              item_id: pckg.id,
              item_type: ShopCartType.Package,
              qty: pckg.qty,
              store_id: pckg?.store_id
            }));
            const products: ShoppingItemCartParams[] = (
              cart?.selectedProducts ?? []
            ).map((prct) => ({
              item_id: prct.id,
              item_type: ShopCartType.Product,
              qty: prct.qty
            }));
            const prevItemsState =
              data.items.map((item: ShopCartItem) => ({
                invoice_id: data.details.invoice_id,
                cart_item_id: item.cart_item_id,
                item_id: item.id,
                item_type: item.item_type,
                qty: item.qty
              })) ?? [];
            const newItemList = [...packages, ...products];

            const { add, update } = processArrays(prevItemsState, newItemList);

            const delay = (ms: number): Promise<void> =>
              new Promise((resolve) => {
                setTimeout(resolve, ms);
              });

            await Promise.race([
              Promise.all(update.map((item) => api.shoppingCartUpdate(item))),
              delay(500)
            ]);

            if (add.length) {
              const params: AddShoppingCartBulkParameters = {
                invoice_id: data.details.invoice_id,
                items_list: add
              };

              const { data: addBulkResponse } =
                await api.shoppingCartAddBulk(params);

              if (!addBulkResponse.error) {
                dispatch(clearCart());
                return { data: addBulkResponse.cart_list };
              }
            }
          }

          dispatch(clearCart());

          return { data };
        } catch (error) {
          console.error(`Error while get shopping cart list: ${error.message}`);

          // If it's a 403 error, the user was logged out by the axios interceptor
          // Return empty cart data instead of error to prevent loader from getting stuck
          if (error?.response?.status === 403) {
            return {
              data: defaultBasketData
            };
          }

          return { error };
        }
      },
      providesTags: ['getShoppingCart']
    }),
    addToShoppingCart: builder.mutation<
      ShopCartUpdateResponse,
      AddShoppingCartParameters
    >({
      queryFn: async (data: AddShoppingCartParameters) => {
        const { item_id, item_type, qty, store_id } = data;
        try {
          const { data: shopCart } = await api.shoppingCartAdd({
            item_id,
            item_type,
            store_id,
            qty
          });

          return { data: shopCart };
        } catch (error) {
          console.error(
            `Error while add product to shop cart: ${error.message}`
          );

          return { error };
        }
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data: response } = await queryFulfilled;

        if (!response.error) {
          dispatch(
            shoppingCartApi.util.updateQueryData(
              'getShoppingCart',
              '',
              (draft) => {
                draft = response.cart_list;
                return draft;
              }
            )
          );
        }
      }
    }),
    updateShoppingCartItem: builder.mutation({
      queryFn: async (data: {
        invoiceId: number;
        cartItemId: number;
        itemId: number;
        itemType: ShopCartType;
        qty: number;
      }) => {
        const { invoiceId, cartItemId, itemId, itemType, qty } = data;
        try {
          const { data: shopCart } = await api.shoppingCartUpdate({
            invoice_id: invoiceId,
            cart_item_id: cartItemId,
            item_id: itemId,
            item_type: itemType,
            qty
          });

          return { data: shopCart };
        } catch (error) {
          console.error(
            `Error while update item in shop cart: ${error.message}`
          );

          return { error };
        }
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data: response } = await queryFulfilled;

        if (!response.error) {
          dispatch(
            shoppingCartApi.util.updateQueryData(
              'getShoppingCart',
              '',
              (draft) => {
                draft = response.cart_list;
                return draft;
              }
            )
          );
        }
      }
    }),
    deleteShoppingCartItem: builder.mutation({
      queryFn: async (
        data: { invoiceId: number; cartItemId: number },
        { dispatch, getState }
      ) => {
        const { invoiceId, cartItemId } = data;
        try {
          const { cart } = getState() as { cart: CartStore };
          const { data: shopCart } = await api.shoppingCartDeleteItem({
            invoice_id: invoiceId,
            cart_item_id: cartItemId
          });

          if (shopCart?.cart_list?.items.length) {
            const discountIds = shopCart?.cart_list?.items
              .filter((item: ShopCartItem) => item.is_promo)
              .reduce((acc: number[], item: ShopCartItem) => {
                if (!acc.includes(item.id)) {
                  acc.push(item?.id);
                }
                return acc;
              }, []);

            const codeTodelete = (cart.promoCodes ?? [])
              .map((promo) => promo.promo_id)
              .reduce((acc: number[], id: number) => {
                if (!discountIds.includes(id)) {
                  acc.push(id);
                }
                return acc;
              }, []);

            codeTodelete.forEach((id: number) => {
              dispatch(removePromoDetails(id));
            });
          } else {
            dispatch(clearPromoDetails());
          }

          return { data: shopCart };
        } catch (error) {
          console.error(
            `Error while delete item from shop cart: ${error.message}`
          );

          return { error };
        }
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data: response } = await queryFulfilled;

        if (!response.error) {
          dispatch(
            shoppingCartApi.util.updateQueryData(
              'getShoppingCart',
              '',
              (draft) => {
                draft = response.cart_list;
                return draft;
              }
            )
          );

          const cartItemIds =
            response.cart_list.items?.map(
              (item: ShopCartItem) => item.cart_item_id
            ) || [];
          dispatch(
            cleanupStalePreferences({
              current_cart_item_ids: cartItemIds,
              invoice_id: response.cart_list.details?.invoice_id || 0
            })
          );
        }
      }
    }),
    getShippingCartList: builder.query<
      CartShippingResponseType[],
      number | undefined
    >({
      queryFn: async (invoice_id) => {
        try {
          const { data } = await api.getShippingList(invoice_id);

          return { data };
        } catch (error) {
          console.error(
            `Error while get shopping cart shipping list: ${error.message}`
          );

          return { error };
        }
      },
      keepUnusedDataFor: 0
    }),
    addShipmentMethod: builder.mutation<
      ShopCartUpdateResponse,
      AddShippingMethodParameters
    >({
      queryFn: async (params: AddShippingMethodParameters) => {
        try {
          const { data } = await api.addShippingMethod(params);

          return { data };
        } catch (error) {
          console.error(
            `Error while add shipping method and address to the order: ${error.message}`
          );

          return { error: error.message };
        }
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data: response } = await queryFulfilled;

        if (response.success) {
          dispatch(
            shoppingCartApi.util.updateQueryData(
              'getShoppingCart',
              '',
              (draft) => {
                draft = response.cart_list;
                return draft;
              }
            )
          );
        }
      }
    }),
    addGiftCardPayment: builder.mutation<
      ShopCartUpdateResponse,
      AddGiftCardMethodParameters
    >({
      queryFn: async (params: AddGiftCardMethodParameters) => {
        try {
          const { data } = await api.addGiftCardPayment(params);

          return { data };
        } catch (error) {
          console.error(
            `Error while add Gift Card payment to the Cart: ${error.message}`
          );

          return { error: error.message };
        }
      },
      invalidatesTags: (result) => (result?.success ? ['getShoppingCart'] : [])
    }),
    removeGiftCardPayment: builder.mutation<
      ShopCartUpdateResponse,
      RemoveGiftCardMethodParameters
    >({
      queryFn: async (params: RemoveGiftCardMethodParameters) => {
        try {
          const { data } = await api.removeGiftCardPayment(params);

          return { data };
        } catch (error) {
          console.error(
            `Error while remove Gift Card from the Cart: ${error.message}`
          );

          return { error: error.message };
        }
      },
      invalidatesTags: (result) => (result?.success ? ['getShoppingCart'] : [])
    }),
    addCreditCardPayment: builder.mutation<
      ShopCartUpdateResponse,
      AddCreditCardMethodParameters
    >({
      queryFn: async (params: AddCreditCardMethodParameters) => {
        try {
          const { data } = await api.addCreditCardPayment(params);

          return { data };
        } catch (error) {
          console.error(
            `Error while add Gift Card payment to the Cart: ${error.message}`
          );

          return { error: error.message };
        }
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data: response } = await queryFulfilled;

        if (response.success) {
          dispatch(
            shoppingCartApi.util.updateQueryData(
              'getShoppingCart',
              '',
              (draft) => {
                draft = response.cart_list;
                return draft;
              }
            )
          );
        }
      }
    }),
    removeCreditCardPayment: builder.mutation<
      ShopCartUpdateResponse,
      RemoveCreditCardMethodParameters
    >({
      queryFn: async (params: RemoveCreditCardMethodParameters) => {
        try {
          const { data } = await api.removeCreditCardPayment(params);

          return { data };
        } catch (error) {
          console.error(
            `Error while add Gift Card payment to the Cart: ${error.message}`
          );

          return { error: error.message };
        }
      },
      invalidatesTags: (result) => (result?.success ? ['getShoppingCart'] : [])
    }),
    getClientPurchaseHistory: builder.query<
      PurchaseHistoryItem[],
      PurchaseHistoryParams
    >({
      queryFn: async (params: PurchaseHistoryParams) => {
        try {
          const { data } = await api.getClPurchaseHistory(params);

          return { data: data?.list ?? [] };
        } catch (error) {
          console.error(
            `Error while get client purchase history: ${error.message}`
          );

          return { error };
        }
      }
    }),
    getClientPurchaseHistoryDetails: builder.query<InvoiceItem[], number>({
      queryFn: async (checkoutId: number) => {
        try {
          const { data } = await api.getClPurchaseHistoryDetails(checkoutId);

          return { data };
        } catch (error) {
          console.error(
            `Error while get client purchase history details: ${error.message}`
          );

          return { error };
        }
      }
    }),
    addPromoCode: builder.mutation<ShopPromoResponseType, ShopPromoParamsType>({
      queryFn: async (params: ShopPromoParamsType, { dispatch }) => {
        try {
          const { data } = await api.validateShopPromoCode(params);

          if (data.success) {
            dispatch(
              setPromoDetails({ ...data, code: params.promo_code ?? '' })
            );
          }

          return { data };
        } catch (error) {
          console.error(
            `Error while validate and add promo code: ${
              error?.response?.data?.error ?? error.message
            }`
          );

          return {
            error:
              error?.response?.data?.promo_error_type ??
              PromoErrorType.DOES_NOT_EXIST
          };
        }
      },
      invalidatesTags: (result) => (result?.success ? ['getShoppingCart'] : [])
    })
  })
});

export const {
  useGetShoppingCartQuery,
  useAddToShoppingCartMutation,
  useUpdateShoppingCartItemMutation,
  useDeleteShoppingCartItemMutation,
  useGetShippingCartListQuery,
  useAddShipmentMethodMutation,
  useAddGiftCardPaymentMutation,
  useRemoveGiftCardPaymentMutation,
  useAddCreditCardPaymentMutation,
  useRemoveCreditCardPaymentMutation,
  useGetClientPurchaseHistoryQuery,
  useGetClientPurchaseHistoryDetailsQuery,
  useAddPromoCodeMutation
} = shoppingCartApi;
